import { Modal, ScrollablePane, Stack, Sticky, StickyPositionType, Text } from '@fluentui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import closeGreyIcon from '../../../assets/icons/svg/closeGrey.svg';
import downloadIcon from '../../../assets/icons/svg/download.svg';
import leftIcon from '../../../assets/icons/svg/left.svg';
import rightIcon from '../../../assets/icons/svg/right.svg';
import zoomInIcon from '../../../assets/icons/svg/zoomIn.svg';
import zoomOutIcon from '../../../assets/icons/svg/zoomOut.svg';
import { useDocumentsManager } from '../../../contexts/documentContext';
import { ResponsiveSize, useResponsiveSize } from '../../../contexts/responsiveContext';
import { saveToComputer } from '../../../helpers';
import SvgIcon from '../../SvgIcon';
import ImageViewer from './ImageViewer';
import PdfViewer from './PdfViewer';

const Viewer = (props) => {

    const {
        document = {},
        isOpen = false,
        onDismiss = () => null,
        onDismissed = () => null
    } = props;

    const { getFileData } = useDocumentsManager();
    const { responsiveSize } = useResponsiveSize();

    const [numPages, setNumPages] = useState(1);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1.95);

    const { creerLe, fileData, nom, typeDocument } = document;

    const extension = useMemo(() => {
        if (!nom) return null;
        return nom.split(".").pop().toLowerCase();
    }, [nom]);

    useEffect(() => {
        if (extension === "pdf") setScale(1.95);
        else setScale(1); // Images
    }, [extension])

    const page = useMemo(() => {
        const activePage = pageNumber || (numPages ? 1 : "--");
        const totalPages = numPages || "--";
        return `${activePage} / ${totalPages}`;
    }, [numPages, pageNumber])

    const styles = useMemo(
        () => ({
            header: { root: { margin: 26 } },
            headerText: { root: { color: "#8A8A8A" } },
            modal: {
                main: {
                    height: "100%",
                    overflowY: "hidden",
                    width: "100%"
                },
                scrollableContent: {
                    selectors: {
                        // Fix bug d'affichage du sticky header lorsque l'utilisateur a préallablement scrollé la table des documents
                        "& > div > div > span": {
                            overflow: "visible !important",
                            width: "100% ! important"
                        }
                    }
                }
            },
            scrollablePane: {
                root: { top: 75 },
                contentContainer: { background: "#D9D9D9" }
            }
        }), []
    );

    const changeScale = useCallback((offset) => setScale(prevScale => prevScale + offset), []);
    const zoomOut = useCallback(() => changeScale(-0.5), [changeScale]);
    const zoomIn = useCallback(() => changeScale(0.5), [changeScale]);

    const changePage = useCallback((offset) => setPageNumber(prevPageNumber => prevPageNumber + offset), []);
    const previous = useCallback(() => changePage(-1), [changePage]);
    const next = useCallback(() => changePage(1), [changePage]);

    const _onDismissed = useCallback(
        () => {
            setNumPages(1);
            if (typeof onDismissed === "function") onDismissed();
        }, [onDismissed]
    );

    const _onLoadSuccess = useCallback(
        ({ numPages }) => {
            setNumPages(numPages);
            setPageNumber(1);
        }, []
    );

    const _onSave = useCallback(
        async () => {
            const fileData = await getFileData(document);
            saveToComputer(fileData, document.nom);
        },
        [document, getFileData]
    );

    const viewer = useMemo(() => {
        if (!extension) return null;
        switch (extension) {
            case "pdf": return <PdfViewer
                fileData={fileData}
                onLoadSuccess={_onLoadSuccess}
                pageNumber={pageNumber}
                scale={scale}
            />;
            case "png":
            case "jpg":
            case "jpeg": return <ImageViewer
                extension={extension}
                fileData={fileData}
                scale={scale}
            />
            default: return null;
        }
    }, [_onLoadSuccess, extension, fileData, pageNumber, scale]);

    return <Modal
        isOpen={isOpen}
        onDismiss={onDismiss}
        onDismissed={_onDismissed}
        styles={styles.modal}
    >
        <Sticky stickyPosition={StickyPositionType.Header}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" styles={styles.header}>
                {responsiveSize === ResponsiveSize.small
                    ? <Text styles={styles.headerText}>{page}</Text>
                    : <Text styles={styles.headerText}><b>{nom}</b> - {typeDocument} - {new Date(creerLe).toLocaleDateString("fr-FR")} - {page}</Text>
                }
                <Stack horizontal tokens={{ childrenGap: 11 }}>
                    <SvgIcon
                        disabled={pageNumber <= 1}
                        onClick={previous}
                        src={leftIcon}
                        title="Page précédente"
                        width={23}
                    />
                    <SvgIcon
                        disabled={pageNumber >= numPages}
                        onClick={next}
                        src={rightIcon}
                        title="Page suivante"
                        width={23}
                    />
                    <SvgIcon
                        onClick={zoomIn}
                        src={zoomInIcon}
                        title="Zoomer"
                    />
                    <SvgIcon
                        disabled={scale <= 1}
                        onClick={zoomOut}
                        src={zoomOutIcon}
                        title="Dézoomer"
                    />
                    <SvgIcon
                        onClick={_onSave}
                        src={downloadIcon}
                        title="Télécharger"
                    />
                    <SvgIcon
                        onClick={onDismiss}
                        src={closeGreyIcon}
                        title="Fermer"
                    />
                </Stack>
            </Stack>
        </Sticky>
        <ScrollablePane styles={styles.scrollablePane}>
            {viewer}
        </ScrollablePane>
    </Modal>;

};

export default Viewer;